import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { ReactComponent as CheckIcon } from '../../../assets/img/icons/checkmark-outline.svg'
import './toast.scss'

const FADEOUT_DURATION = 250

const Toast = ({
                 message,
                 icon,
                 variant = 'success',
                 duration = 4000,
                 onClose,
               }) => {
  const timerRef = useRef(null)
  // Keep the latest onClose without retriggering the auto-dismiss timer: the
  // container passes a fresh onClose closure on every render, and including it
  // in the effect deps would reset every toast's timer whenever a new toast appears.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const [isDisappearing, setIsDisappearing] = useState(false)

  useEffect(() => {
    if (!duration) return
    timerRef.current = setTimeout(() => {
      setIsDisappearing(true)
    }, duration)
    return () => clearTimeout(timerRef.current)
  }, [duration])

  useEffect(() => {
    if (isDisappearing) {
      const closeTimer = setTimeout(() => {
        onCloseRef.current?.()
      }, FADEOUT_DURATION)
      return () => clearTimeout(closeTimer)
    }
  }, [isDisappearing])

  const dismiss = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    setIsDisappearing(true)
  }

  const className = classnames(`toast toast-${variant}`, {
    'is-disappearing': isDisappearing,
  })

  return (
    <div onClick={dismiss} className={className} role="status" aria-live="polite">
      <div className="toast-icon">
        {icon || <CheckIcon/>}
      </div>
      <div className="toast-content">
        <span className="toast-message">{message}</span>
      </div>
      {duration > 0 && (
        <div className="toast-progress" aria-hidden="true">
          <div
            className="toast-progress-bar"
            style={{ '--animation-duration': `${duration}ms` }}
          />
        </div>
      )}
    </div>
  )
}

Toast.defaultProps = {
  variant: 'success',
}

Toast.propTypes = {
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  icon: PropTypes.node,
  variant: PropTypes.oneOf(['success', 'error']),
  duration: PropTypes.number,
  onClose: PropTypes.func,
}

export default Toast
